import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResetRecoilState, useSetRecoilState } from 'recoil';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { clampString, cn } from '@/lib/utils';
import { CreateWorkspaceModal } from '@/views/settings/create-workspace-modal';
import NewMemberModal from '@/views/settings/team/components/new-add-member-modal';
import { IOption } from '@/views/home/dashboard/components/datasource-dropdown';
import { Business } from '@sparrow-marketing/contracts';
import {
  ClientContainer,
  TeamContainer,
  UserContainer,
  ReportsContainer,
} from '@/providers';
import { trackEvent } from '@/helpers/external-tools-helper';
import { ChartsState, SelectedInsightsState, useInsights } from '@/states';
import { LOCAL_STORAGE_TYPES, APP_ROUTE, COOKIE_KEYS } from '@/constants';
import useLocalStorage from '@/hooks/localstorage';
import {
  FORBIDDEN_QUERY_PARAMS_ROUTES,
  QUERY_PARAMS,
  parseQueryParameters,
  getQueryParameters,
  mergeParameters,
} from '@/helpers/query-params-utils';
import { useEffect, useMemo } from 'react';
import TeamUpgradeDialog from '@/views/settings/team/components/team-upgrade-dialog';
import { useGetMembers } from '@/hooks/api/team';
import { useTranslation } from 'react-i18next';
import { useHotkeys } from 'react-hotkeys-hook';
import { useRefreshSession, useSession } from '@/states/common';
import { getSubscriptionName } from '@/views/settings/billing/utils';
import ContactSupportModal from '@/views/settings/team/components/contact-support-modal';
import { useTeamLimit } from '@/hooks/use-team-limit';

const { CLIENT } = QUERY_PARAMS;

interface Workspace {
  id: number;
  name: string;
  logo?: string;
  members?: number;
  verified?: boolean;
  workspaceId?: number;
  value: string | number;
  disabled?: boolean;
  accounts?: Array<any>;
}

export function WorkspaceSwitcher({ workspaces }: { workspaces: any }) {
  const { t } = useTranslation('workspace');
  const sidebar = useSidebar();
  const [activeTeam, setActiveTeam] = React.useState(workspaces[0]);
  const [
    showCreateWorkspaceModal,
    setShowCreateWorkspaceModal,
  ] = React.useState(false);
  const [showInviteMembersModal, setShowInviteMembersModal] = React.useState(
    false
  );
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [planName, setPlanName] = React.useState<'professional' | 'team'>(
    'professional'
  );
  const [isLoadingWorkspace, setIsLoadingWorkspace] = React.useState(false);
  const navigate = useNavigate();
  const { state: userState } = UserContainer.useContainer();
  const {
    state: clientState,
    updateSelectedClient,
    updatingSelectedClient,
  } = ClientContainer.useContainer();
  const { state: teamState } = TeamContainer.useContainer();
  const teamAmount = teamState?.members?.length - 1;

  const { clients } = clientState;
  const { get, set } = useLocalStorage();
  const localStorageClient = get(LOCAL_STORAGE_TYPES.WORKSPACE) ?? {};
  const setSelectedInsightsState = useSetRecoilState(SelectedInsightsState);
  const resetChart = useResetRecoilState(ChartsState);
  const { clearInsights } = useInsights();
  const { resetReportsState } = ReportsContainer.useContainer();
  const { data: session } = useSession();
  const { permission, billing: billingInfo } = session;
  const location = useLocation();
  const refreshSession = useRefreshSession();
  const getMembers = useGetMembers();
  const businessId = userState?.business?.id;
  const isLegacyUser = userState?.business?.billing?.isLegacyUser;
  const [showContactSalesModal, setShowContactSalesModal] = React.useState(
    false
  );
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const {
    isLimitMembers,
    isAppSumoCustomer: isAppSumo,
    isTenCodePurchaser: isTenCode,
  } = useTeamLimit({
    teamAmount,
    userState,
  });

  useEffect(() => {
    if (businessId) {
      getMembers(businessId);
    }
  }, [businessId]);

  const isWorkspaceNotAllowed = useMemo(() => {
    let subscriptionPlanName = '';
    if (isLegacyUser) {
      subscriptionPlanName = getSubscriptionName(
        userState?.business?.billing?.status,
        userState?.business?.billing?.subscriptionName
      ).plan.toLowerCase();
    } else {
      subscriptionPlanName = userState?.business?.billing?.paddleSubscription?.planName?.toLowerCase();
    }
    return (
      !subscriptionPlanName?.includes('team') &&
      !subscriptionPlanName?.includes('professional')
    );
  }, [userState?.business?.billing]);

  const userRole = permission?.role ?? 'member';

  const isMac = useMemo(() => {
    return navigator.platform.toLowerCase().includes('mac');
  }, []);

  const handleAccountLinkClick = (workspace: Workspace): void => {
    // Show loader
    setIsLoadingWorkspace(true);

    // Track the workspace switch event immediately
    trackEvent('navigation workspace tab', {
      clientId: clientState.selectedClient?.id,
      clientName: clientState.selectedClient?.name,
      businessId: clientState.selectedClient?.businessId,
      id: workspace.id,
      name: workspace.name,
    });

    // Add delay before processing the workspace change
    setTimeout(() => {
      setSelectedInsightsState({});
      clearInsights();
      resetChart();
      resetReportsState();

      const clientId = workspace.id;

      if (clientId !== clientState.selectedClient?.id) {
        updatingSelectedClient();
        updateSelectedClient(clientId);
      }

      // Check if current path includes analytics and redirect to dashboard
      const isAnalyticsRoute = location.pathname.includes(
        `/${APP_ROUTE.ANALYTICS}`
      );
      const newPath = isAnalyticsRoute
        ? `/ws/${clientId}/${APP_ROUTE.ANALYTICS}/${APP_ROUTE.DASHBOARD}`
        : location.pathname.replace(/\/ws\/[^/]+/, `/ws/${clientId}`);

      navigate(newPath);

      refreshSession();
      setIsLoadingWorkspace(false);
      // TODO: remove this once we have a better way to handle the workspace switch
      window.location.hash = ''; // remove hash from url, remove old hash states

      // setActiveTeam(workspace);
    }, 1000); // 800ms delay feels responsive while still showing the loader
  };

  const hotkeyCombinations = clients
    .map((_, index: number) => `alt+${index + 1}`)
    .join(',');

  useHotkeys(
    hotkeyCombinations,
    event => {
      event.preventDefault();
      const clientIndex = parseInt(event.code.slice(-1), 10) - 1;
      if (clientIndex >= 0 && clientIndex < clients.length) {
        handleAccountLinkClick(clients[clientIndex]);
      }
    },
    {
      preventDefault: true,
    },
    [clients, handleAccountLinkClick]
  );
  return (
    <div>
      {isLoadingWorkspace && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999]">
          <div className="absolute inset-0 bg-black bg-opacity-10 backdrop-blur-sm" />
          <div className="bg-white bg-opacity-90 p-8 rounded-lg shadow-xl flex flex-col items-center relative z-10">
            <div className="flex space-x-2 mb-4">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" />
              <div
                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: '0.2s' }}
              />
              <div
                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: '0.4s' }}
              />
            </div>
            <p className="text-gray-700 font-medium">Switching workspace...</p>
          </div>
        </div>
      )}
      <SidebarMenu>
        <SidebarMenuItem className={cn(!sidebar.open && 'mx-auto')}>
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              {sidebar.open ? (
                <Button variant="outline" intent="tertiary" asChild>
                  <div className="flex items-center justify-center w-full gap-[12px] p-[12px] h-auto">
                    <div className="flex aspect-square size-8 text-grayscale-600 items-center justify-center rounded-lg bg-grayscale-50">
                      {clientState.selectedClient?.logo ? (
                        <img
                          src={clientState.selectedClient?.logo}
                          alt={clientState.selectedClient?.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <i className="ti ti-building text-grayscale-600" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-gray-900">
                          {clampString(clientState.selectedClient?.name, 19)}
                        </span>
                        <span className="text-xs font-normal text-gray-500">
                          {clampString(userState?.business?.name, 24)}
                        </span>
                      </div>
                    </div>
                    <i className="ti ti-selector text-gray-400 text-lg" />
                  </div>
                </Button>
              ) : (
                <SidebarMenuButton
                  className={cn(
                    !sidebar.open &&
                      'mt-4 justify-center group-data-[collapsible=icon]:!size-11 bg-white border border-gray-200 rounded-lg'
                  )}
                >
                  {clientState.selectedClient?.logo ? (
                    <img
                      src={clientState.selectedClient?.logo}
                      alt={clientState.selectedClient?.name}
                      className="w-7 h-7 object-cover rounded-lg border border-gray-200"
                    />
                  ) : (
                    <div className="bg-gradient-to-r from-blue-500 to-blue-300 p-4 rounded-lg">
                      <i className="ti ti-building text-gray-50" />
                    </div>
                  )}
                </SidebarMenuButton>
              )}
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-[250px] rounded-lg font-sans py-2 px-0"
              align="start"
              side={sidebar.isMobile ? 'bottom' : 'bottom'}
              sideOffset={4}
            >
              <div className="flex flex-col">
                <div className="max-h-[180px] overflow-y-auto">
                  {clients?.map((workspace: Workspace, index: number) => (
                    <DropdownMenuItem
                      key={workspace.id}
                      onClick={() => handleAccountLinkClick(workspace)}
                      className="w-full flex items-center gap-3 rounded-lg cursor-pointer px-4 focus:bg-primary-50 focus:rounded-none"
                    >
                      <div className="flex aspect-square size-10 items-center justify-center rounded-lg bg-gradient-to-b from-blue-400 to-blue-500">
                        {workspace.logo && (
                          <img
                            src={workspace.logo ?? 'https://placehold.co/40x40'}
                            alt={workspace.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-semibold text-gray-900">
                            {workspace.name}
                          </span>
                          {workspace.verified && (
                            <i className="ti ti-check text-blue-500" />
                          )}
                        </div>
                        <span className="text-xs font-normal text-gray-500">
                          Data Sources ({workspace?.accounts?.length})
                        </span>
                      </div>
                      {workspace.workspaceId && (
                        <div className="flex items-center gap-1 text-gray-400">
                          <i className="ti ti-apps" />
                          <span>{workspace.workspaceId}</span>
                        </div>
                      )}
                      {clientState.selectedClient?.id === workspace.id && (
                        <i className="ti ti-circle-check-filled text-primary text-lg" />
                      )}
                      {clientState.selectedClient?.id !== workspace.id && (
                        <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 border px-1.5 font-mono text-xs font-medium text-muted-foreground bg-white opacity-100 rounded-sm">
                          <span className="text-xs">{isMac ? '⌥' : 'Alt'}</span>
                          <span className="text-xs">{index + 1}</span>
                        </kbd>
                      )}
                    </DropdownMenuItem>
                  ))}
                </div>

                <DropdownMenuSeparator />

                {userRole !== 'member' && (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        navigate(
                          `${APP_ROUTE.SETTINGS}/${APP_ROUTE.WORKSPACE}`
                        );
                      }}
                      className="w-full flex items-center gap-3 rounded-lg cursor-pointer px-4 py-3 focus:bg-primary-50 focus:rounded-none"
                    >
                      <div className="flex aspect-square items-center justify-center rounded-lg">
                        <i className="ti ti-settings text-lg text-gray-600" />
                      </div>
                      <span className="flex-1 text-left font-normal text-gray-900">
                        {t('workspace_settings', 'Workspace settings')}
                      </span>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => {
                        if (isLimitMembers) {
                          if (isAppSumo || isTenCode) {
                            setShowContactSalesModal(true);
                          } else {
                            setPlanName('team');
                            setShowUpgradeModal(true);
                          }
                        } else {
                          setShowInviteMembersModal(true);
                        }
                      }}
                      className="w-full flex items-center gap-3 rounded-lg cursor-pointer px-4 py-3 focus:bg-primary-50 focus:rounded-none"
                    >
                      <div className="flex aspect-square items-center justify-center rounded-lg">
                        <i className="ti ti-user-plus text-lg text-gray-600" />
                      </div>
                      <span className="flex-1 text-left font-normal text-gray-900">
                        {t('invite_members', 'Invite Members')}
                      </span>
                    </DropdownMenuItem>

                    <div className="px-4">
                      <Button
                        intent="secondary"
                        variant="outline"
                        className="w-full mt-2 flex items-center justify-center gap-2 p-2 text-primary border-primary hover:bg-primary-50"
                        onClick={() => {
                          if (isWorkspaceNotAllowed) {
                            setPlanName('professional');
                            setShowUpgradeModal(true);
                          } else {
                            setShowCreateWorkspaceModal(true);
                          }
                        }}
                        startIcon={<i className="ti ti-plus" />}
                      >
                        {t('create_workspace', 'Create Workspace')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <CreateWorkspaceModal
        open={showCreateWorkspaceModal}
        onOpenChange={setShowCreateWorkspaceModal}
        onComplete={() => {
          setShowCreateWorkspaceModal(false);
          setIsDropdownOpen(false);
        }}
      />
      <NewMemberModal
        isOpen={showInviteMembersModal}
        onClose={() => setShowInviteMembersModal(false)}
      />
      <TeamUpgradeDialog
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        planName={planName}
      />
      <ContactSupportModal
        open={showContactSalesModal}
        onOpenChange={setShowContactSalesModal}
      />
    </div>
  );
}
